import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { authService } from "@/services/authService";
import type { User } from "@/services/authService";
import { toast } from "sonner";
import { PersonalDataUploadConsentDialog } from "@/components/common/PersonalDataUploadConsentDialog";
import { StepProgress } from "./components/StepProgress";
import { PasswordStep } from "./steps/PasswordStep";
import { KtpAddressStep } from "./steps/KtpAddressStep";
import { EmailStep } from "./steps/EmailStep";
import { PhoneStep } from "./steps/PhoneStep";
import { PhotosStep } from "./steps/PhotosStep";

interface OnboardingWizardProps {
  user?: User | null;
  onComplete: () => void;
}

type Step = "password" | "ktpAddress" | "email" | "phone" | "photos";

export function OnboardingWizard({ user, onComplete }: OnboardingWizardProps) {
  const [userData, setUserData] = React.useState<User | null>(user || null);
  const [forcePwdAfterForgotIar, setForcePwdAfterForgotIar] = React.useState(() => {
    if (typeof window === "undefined") return false;
    try {
      return sessionStorage.getItem("orari_prompt_password_after_forgot_iar") === "1";
    } catch {
      return false;
    }
  });

  // Update userData when user prop changes
  React.useEffect(() => {
    if (user) {
      setUserData(user);
    }
  }, [user]);

  // Fetch full user data if needed
  React.useEffect(() => {
    const fetchUserData = async () => {
      const currentUserData = userData || user;
      if (!currentUserData || !currentUserData.jenis_kelamin) {
        try {
          const fetchedUser = await authService.getCurrentUser();
          if (fetchedUser) {
            setUserData(fetchedUser);
          }
        } catch (error) {
          console.error("Failed to fetch user data:", error);
        }
      }
    };
    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Determine which steps need to be completed
  const needsPassword = userData?.isPasswordSet === false || forcePwdAfterForgotIar;
  const needsKtpAddress = !userData?.ktp || userData?.isSetCompleteAddress === false;
  const needsEmail = userData?.isUpdatedEmail === false;
  const needsPhone = !userData?.no_hp;
  const needsPhotos = userData?.isUpdatePhotos === false;
  const isFotoRejected = userData?.isFotoValid === false;

  const steps: Step[] = [];
  if (needsPassword) steps.push("password");
  if (needsKtpAddress) steps.push("ktpAddress");
  if (needsEmail) steps.push("email");
  if (needsPhone) steps.push("phone");
  if (needsPhotos) steps.push("photos");

  const [currentStepIndex, setCurrentStepIndex] = React.useState(0);
  const currentStep = steps[currentStepIndex];

  // Consent dialog state
  const [consentOpen, setConsentOpen] = React.useState(false);
  const [consentConfirmLabel, setConsentConfirmLabel] = React.useState("Lanjutkan unggah");
  const afterConsentRef = React.useRef<null | (() => Promise<void>)>(null);

  const runAfterConsent = async () => {
    const fn = afterConsentRef.current;
    afterConsentRef.current = null;
    setConsentOpen(false);
    if (fn) await fn();
  };

  const handleConsentNeeded = (fn: () => Promise<void>, label: string) => {
    afterConsentRef.current = fn;
    setConsentConfirmLabel(label);
    setConsentOpen(true);
  };

  // Generate personalized greeting
  const getGreeting = (): string => {
    if (!userData || !userData.jenis_kelamin) {
      return "Selamat Datang! 👋";
    }
    const name = userData.name || "";
    if (userData.jenis_kelamin === "L") {
      return `Selamat Datang! 👋 OM ${name}`;
    } else {
      return `Selamat Datang! 👋 YL ${name}`;
    }
  };

  // Get step description
  const getStepDescription = (): string => {
    const totalSteps = steps.length;
    const currentNumber = currentStepIndex + 1;
    const stepNames: Record<Step, string> = {
      password: "Password",
      ktpAddress: "KTP & Alamat",
      email: "Email",
      phone: "Nomor HP",
      photos: "Foto",
    };
    if (!currentStep) {
      return "Selesai";
    }
    return `${stepNames[currentStep]} • Langkah ${currentNumber} dari ${totalSteps}`;
  };

  const advanceStep = () => {
    setCurrentStepIndex(currentStepIndex + 1);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/50">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle className="text-2xl font-bold">{getGreeting()}</CardTitle>
          <CardDescription>{getStepDescription()}</CardDescription>
        </CardHeader>
        <CardContent>
          <StepProgress steps={steps} currentStepIndex={currentStepIndex} />

          {currentStep === "password" && (
            <PasswordStep
              userData={userData}
              forcePwdAfterForgotIar={forcePwdAfterForgotIar}
              onSuccess={(wasForceRecovery) => {
                setForcePwdAfterForgotIar(false);
                if (wasForceRecovery) {
                  onComplete();
                } else {
                  advanceStep();
                }
              }}
              onError={(msg) => toast.error(msg)}
              onUserDataUpdate={(u) => setUserData(u)}
            />
          )}

          {currentStep === "ktpAddress" && (
            <KtpAddressStep
              userData={userData}
              onSuccess={advanceStep}
              onError={(msg) => toast.error(msg)}
              onConsentNeeded={handleConsentNeeded}
            />
          )}

          {currentStep === "email" && (
            <EmailStep
              userData={userData}
              onSuccess={advanceStep}
              onError={(msg) => toast.error(msg)}
            />
          )}

          {currentStep === "phone" && (
            <PhoneStep
              userData={userData}
              onSuccess={advanceStep}
              onError={(msg) => toast.error(msg)}
            />
          )}

          {currentStep === "photos" && (
            <PhotosStep
              userData={userData}
              isFotoRejected={isFotoRejected}
              onSuccess={onComplete}
              onError={(msg) => toast.error(msg)}
              onConsentNeeded={handleConsentNeeded}
              onSkip={onComplete}
            />
          )}
        </CardContent>
      </Card>

      <PersonalDataUploadConsentDialog
        open={consentOpen}
        onOpenChange={(open) => {
          if (!open) afterConsentRef.current = null;
          setConsentOpen(open);
        }}
        onConfirm={() => {
          void runAfterConsent();
        }}
        confirmLabel={consentConfirmLabel}
      />
    </div>
  );
}
